import React, { useEffect, useMemo, useState } from 'react'
import api from '../lib/api'

const fallbackPlans = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Acesso inicial ao estúdio BNCC, atendimento básico e trilhas por série.',
    price: 29.9,
    currency: 'BRL',
    features: { maxActivities: 24, maxGames: 12, maxBlockly: 12, hasAI: true, hasProfessionals: false, hasVideoCall: false }
  },
  {
    id: 'family',
    name: 'Family',
    description: 'Assinatura com atendimento on-line, mais exercícios e trilhas adaptadas.',
    price: 59.9,
    currency: 'BRL',
    features: { maxActivities: 120, maxGames: 48, maxBlockly: 48, hasAI: true, hasProfessionals: true, hasVideoCall: true }
  },
  {
    id: 'school',
    name: 'School',
    description: 'Plano ampliado para professor, coordenação e mais créditos de geração.',
    price: 119.9,
    currency: 'BRL',
    features: { maxActivities: 300, maxGames: 120, maxBlockly: 120, hasAI: true, hasProfessionals: true, hasVideoCall: true }
  },
  {
    id: 'free',
    name: 'Gratuito',
    description: 'Acesso livre para testar conteúdos por série e atendimento em modo local.',
    price: 0,
    currency: 'BRL',
    features: { maxActivities: 12, maxGames: 4, maxBlockly: 4, hasAI: false, hasProfessionals: false, hasVideoCall: false }
  }
]

const fallbackCreditBundles = [
  { id: 'credits-40', name: 'Pacote 40 créditos', credits: 40, price: 19.9, bonus: 0 },
  { id: 'credits-120', name: 'Pacote 120 créditos', credits: 120, price: 49.9, bonus: 10 },
  { id: 'credits-300', name: 'Pacote 300 créditos', credits: 300, price: 99.9, bonus: 40 }
]

function saveLocalOrder(payload) {
  const current = JSON.parse(localStorage.getItem('mathkids.localCommerce') || '[]')
  current.unshift({ id: `local-order-${Date.now()}`, createdAt: new Date().toISOString(), ...payload })
  localStorage.setItem('mathkids.localCommerce', JSON.stringify(current.slice(0, 50)))
}

function priceLabel(plan) {
  return plan.price === 0 ? 'Grátis' : `R$ ${Number(plan.price).toFixed(2)}`
}

export default function PricingPage({ user }) {
  const [plans, setPlans] = useState([])
  const [creditBundles, setCreditBundles] = useState(fallbackCreditBundles)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [checkoutLoading, setCheckoutLoading] = useState('')

  useEffect(() => { fetchPlans() }, [])

  const visiblePlans = useMemo(() => {
    const unique = new Map()
    ;(plans.length ? plans : fallbackPlans).forEach((plan) => unique.set(plan.id, plan))
    return Array.from(unique.values())
  }, [plans])

  const fetchPlans = async () => {
    try {
      setLoading(true)
      const response = await api.get('/subscriptions/plans')
      const list = response.data.plans || []
      setPlans(list.length ? list : fallbackPlans)
      setMessage(list.length ? '' : 'Planos carregados em modo local enquanto a API não responde.')
    } catch (error) {
      setPlans(fallbackPlans)
      setMessage('Planos e pacotes de créditos carregados em modo local enquanto a API não responde.')
    } finally {
      setLoading(false)
    }
  }

  const handleCheckout = async (planId) => {
    if (!user) {
      setMessage('Entre na conta para contratar uma assinatura ou comprar créditos.')
      return
    }

    try {
      setCheckoutLoading(planId)
      setMessage('')
      const { data } = await api.post('/subscriptions/checkout', { planId })
      const initPoint = data?.preference?.initPoint
      if (!initPoint) {
        saveLocalOrder({ kind: 'subscription', planId, status: 'pending-link' })
        setMessage('Assinatura criada, mas o link de pagamento não foi retornado. O pedido ficou salvo localmente.')
        return
      }
      window.location.href = initPoint
    } catch (error) {
      saveLocalOrder({ kind: 'subscription', planId, status: 'local-pending' })
      setMessage(error.response?.data?.error || 'Não foi possível iniciar o checkout agora. A assinatura foi registrada localmente para sincronização.')
    } finally {
      setCheckoutLoading('')
    }
  }

  const handleCredits = async (bundle) => {
    if (!user) {
      setMessage('Entre na conta para comprar créditos extras.')
      return
    }
    try {
      setCheckoutLoading(bundle.id)
      const { data } = await api.post('/credits/purchase', {
        bundleId: bundle.id,
        bundleName: bundle.name,
        credits: bundle.credits,
        bonus: bundle.bonus || 0,
        price: bundle.price
      })
      const initPoint = data?.preference?.initPoint
      if (initPoint) {
        window.location.href = initPoint
        return
      }
      setMessage(data.message || `Pacote ${bundle.name} iniciado com sucesso.`)
    } catch (error) {
      saveLocalOrder({ kind: 'credits', bundleId: bundle.id, credits: bundle.credits + bundle.bonus, status: 'local-pending' })
      setMessage(error.response?.data?.error || `Pacote ${bundle.name} registrado em modo local para sincronização.`)
    } finally {
      setCheckoutLoading('')
    }
  }

  if (loading) return <div className="loading-screen">Carregando assinaturas e créditos...</div>

  return (
    <div className="page-stack">
      <section className="section-title-wrap center">
        <span className="eyebrow">Assinaturas e créditos</span>
        <h1>Planos do portal e pacotes de créditos.</h1>
        <p>Assinaturas liberam trilhas, atendimento, editores e geração extra. Créditos servem para novos exercícios e fluxos premium.</p>
      </section>

      {message ? <div className="alert info">{message}</div> : null}

      <section className="page-stack">
        <h2>Assinaturas</h2>
        <div className="grid cols-4">
          {visiblePlans.map((plan) => (
            <div key={plan.id} className="surface-card compact">
              <div className="section-head">
                <div>
                  <h3>{plan.name}</h3>
                  <p>{plan.description}</p>
                </div>
                {plan.price > 0 ? <span className="badge badge-info">Assinatura</span> : <span className="badge">Teste</span>}
              </div>
              <p><strong>{priceLabel(plan)}</strong>{plan.price > 0 ? '/mês' : ''}</p>
              <ul className="list-soft">
                <li>{plan.features.maxActivities} atividades</li>
                <li>{plan.features.maxBlockly} exercícios em blocos</li>
                <li>{plan.features.maxGames} desafios em games</li>
                <li>{plan.features.hasAI ? 'IA educacional inclusa' : 'Sem IA nesta faixa'}</li>
                <li>{plan.features.hasProfessionals ? 'Atendimento com profissionais' : 'Sem atendimento profissional'}</li>
                <li>{plan.features.hasVideoCall ? 'Videochamada liberada' : 'Sem videochamada'}</li>
              </ul>
              <button onClick={() => handleCheckout(plan.id)} disabled={checkoutLoading === plan.id} className="btn btn-primary w-full">
                {checkoutLoading === plan.id ? 'Processando...' : plan.price === 0 ? 'Ativar plano grátis' : 'Assinar plano'}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="page-stack">
        <h2>Pacotes de créditos</h2>
        <div className="grid cols-3">
          {creditBundles.map((bundle) => (
            <div key={bundle.id} className="surface-card compact">
              <h3>{bundle.name}</h3>
              <p>{bundle.credits} créditos{bundle.bonus ? ` + bônus de ${bundle.bonus}` : ''}</p>
              <p><strong>R$ {Number(bundle.price).toFixed(2)}</strong></p>
              <p>Use para gerar listas extras, módulos sob demanda e personalizações do professor.</p>
              <button onClick={() => handleCredits(bundle)} disabled={checkoutLoading === bundle.id} className="btn btn-secondary w-full">{checkoutLoading === bundle.id ? 'Processando...' : 'Comprar créditos'}</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
